import { createPortal } from "react-dom";

import { Fragment, useContext, useEffect, useRef } from "react"
import { NotificationContext } from "@context"
import { Transition } from "@headlessui/react"
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid"

const types = {
  danger: {
    color: "border-red-500",
    icon: <XCircleIcon className="text-red-500 w-14 h-14" aria-hidden="true" />,
  },

  info: {
    color: "border-blue-500",
    icon: <InformationCircleIcon className="text-blue-500 w-14 h-14" aria-hidden="true" />,
  },

  success: {
    color: "border-green-500",
    icon: <CheckCircleIcon className="text-green-500 w-14 h-14" aria-hidden="true" />,
  },

  warning: {
    color: "border-orange-500",
    icon: <ExclamationCircleIcon className="text-orange-500 w-14 h-14" aria-hidden="true" />,
  },
}

const Notification = () => {
  const { onCloseCallBack, notification, setOpen } = useContext(NotificationContext)
  const type = types[notification.type] || types["info"]
  const timeoutRef = useRef(null)
  
  const handleClose = () => {
    setOpen(false)
    onCloseCallBack()
  }

  useEffect(() => {
    if (notification.open && notification.timeout > 0) {
      timeoutRef.current = setTimeout(() => {
        handleClose()
      }, notification.timeout) // 5000 milliseconds = 5 seconds 
    }

    // Cleanup function to clear the timer if the component unmounts or notification changes
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    };
  }, [notification.open, notification.timeout, setOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  return createPortal(
    <div
      aria-live="assertive"
      className="pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6 z-[101]"
    >
      <div className="flex flex-col items-center w-full space-y-4 sm:items-end">
        <Transition
          show={notification.open}
          as={Fragment}
          enter="transform ease-out duration-300 transition"
          enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
          enterTo="translate-y-0 opacity-100 sm:translate-x-0"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={`pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-content2 shadow-lg ring-1 ring-black ring-opacity-5 border-l-8 border-solid ${type.color}`}
          >
            <div className="p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">{type.icon}</div>

                <div className="ml-3 w-0 flex-1 pt-0.5">
                  <p className="text-lg font-medium text-foreground">{notification.title}</p>
                  <p className="mt-1 text-sm text-content2-foreground">{notification.content}</p>
                </div>

                <div className="flex flex-shrink-0 ml-4">
                  <button
                    type="button"
                    className="inline-flex rounded-md text-foreground bg-content2 hover:text-content2-foreground focus:outline-none"
                    onClick={() => handleClose()}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="w-5 h-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </div>
  , document.body)
}

export { Notification }
